import {
  Button,
  Checkbox,
  Col,
  Collapse,
  Form,
  Input,
  Row,
  Space,
  Switch,
  TimePicker,
} from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectSelectedTenantId } from "../../../store/user/selectors";
import { editBranch, saveBranch } from "../../../store/branch/thunks";
import Modal from "../../../components/generic/Modal";
import { CloseOutlined } from "@ant-design/icons";

import { StyledBranchModalDiv } from "./styles";
import {
  branchInitialValues,
  convertHHmmToDayjs,
  convertToHHmm,
} from "./helper";
import { useRegions } from "../../../hooks/useRegions";
import Select from "../../../components/generic/Select";
import { days, timezoneOptions } from "../../../constant";
import PhoneInput from "../../../components/shared/PhoneInput";
const AddEditBranchModal = (props) => {
  const { editingBranch, handleCancel, mainBranchExists, usersOptions } = props;

  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const { regionsOptions } = useRegions();

  const selectedTenantId = useSelector(selectSelectedTenantId);
  const isEdit = useMemo(() => !!editingBranch?.id, [editingBranch]);

  useEffect(() => {
    if (editingBranch.id) {
      form.setFieldsValue({
        ...editingBranch,
        working_hours: convertHHmmToDayjs(editingBranch?.working_hours || {}),
      });
    }
  }, [editingBranch]);

  const handleSubmit = useCallback(
    async (values) => {
      try {
        setSubmitting(true);

        const payload = {
          ...values,
          is_main_branch: values?.is_main_branch || false,
          working_hours: convertToHHmm(values?.working_hours || {}),
        };
        await dispatch(
          isEdit
            ? editBranch({ ...payload, branch_id: editingBranch.id })
            : saveBranch({ ...payload, tenant_id: selectedTenantId })
        );

        handleCancel();
      } finally {
        setSubmitting(false);
      }
    },
    [isEdit, editingBranch, selectedTenantId]
  );

  return (
    <Modal
      isProcessing={submitting}
      title={isEdit ? "Edit Branch" : "Add New Branch"}
      onCancel={handleCancel}
      footer={null}
      width="90%"
      open
      centered
    >
      <StyledBranchModalDiv>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          disabled={submitting}
          initialValues={branchInitialValues}
        >
          <Row gutter={[24, 24]}>
            {/* DETAILS Section */}
            <Col xs={24} md={12} lg={8}>
              <div className="section-title">DETAILS</div>

              <Form.Item
                label="Branch Name"
                name="name"
                rules={[
                  { required: true, message: "Please enter branch name" },
                ]}
              >
                <Input placeholder="e.g. Manchester Central" />
              </Form.Item>
              {(!mainBranchExists || editingBranch?.is_main_branch) && (
                <Form.Item name="is_main_branch" valuePropName="checked">
                  <Checkbox>Is Main Branch</Checkbox>
                </Form.Item>
              )}

              <Form.Item
                label="Branch Manager"
                name="branch_manager_id"
                required
                rules={[
                  { required: true, message: "Please select branch manger" },
                ]}
              >
                <Select options={usersOptions} placeholder="Search user..." />
              </Form.Item>

              {isEdit && (
                <Form.Item
                  label="Internal Branch Code (Custom)"
                  name="branch_code"
                  rules={[
                    {
                      required: true,
                      message: "Please enter Internal Branch Code",
                    },
                  ]}
                >
                  <Input placeholder="BR-XXXX" disabled />
                </Form.Item>
              )}
            </Col>

            {/* LOCATION Section */}
            <Col xs={24} md={12} lg={8}>
              <div className="section-title">LOCATION</div>

              <Form.Item
                label="Region"
                name="region_id"
                rules={[{ required: true, message: "Please select region" }]}
              >
                <Select placeholder="Select Region" options={regionsOptions} />
              </Form.Item>
              <Form.Item
                label="Address"
                name="address"
                rules={[
                  { required: true, message: "Please enter street address" },
                ]}
              >
                <Input placeholder="Street address" />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="City"
                    name="city"
                    rules={[{ required: true, message: "Please enter city" }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Postal Code"
                    name="postal_code"
                    rules={[
                      { required: true, message: "Please enter postal code" },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="Branch Email"
                name="email"
                rules={[
                  { required: true, message: "Please enter email" },
                  { type: "email", message: "Please enter valid email" },
                ]}
              >
                <Input placeholder="branch@acme.com" />
              </Form.Item>

              <PhoneInput />
            </Col>

            {/* OPERATIONS Section */}
            <Col xs={24} md={24} lg={8}>
              <div className="section-title">OPERATIONS</div>

              <Form.Item
                label="Timezone"
                name="timezone"
                rules={[{ required: true, message: "Please select timezone" }]}
              >
                <Select
                  placeholder="Select Timezone"
                  options={timezoneOptions}
                />
              </Form.Item>

              <div className="operating-hours-header">
                <span>Operating Hours</span>
              </div>

              <Collapse
                defaultActiveKey={["schedule"]}
                items={[
                  {
                    key: "schedule",
                    label: "Weekly Schedule",
                    children: (
                      <div className="schedule-scroll">
                        {days.map((day) => (
                          <div key={day} className="day-section">
                            <div className="day-header">
                              <span className="day-name">{day}</span>
                              <Form.Item
                                name={["working_hours", day, "enabled"]}
                                valuePropName="checked"
                                noStyle
                              >
                                <Switch size="small" />
                              </Form.Item>
                            </div>

                            <Form.List name={["working_hours", day, "slots"]}>
                              {(fields, { add, remove }) => (
                                <>
                                  {fields.map(({ key, name, ...restField }) => (
                                    <div
                                      key={key}
                                      className="time-slot-container"
                                    >
                                      <Form.Item
                                        {...restField}
                                        name={[name, "start"]}
                                        noStyle
                                      >
                                        <TimePicker
                                          format="HH:mm"
                                          placeholder="09:00 AM"
                                          className="time-picker"
                                          use12Hours={false}
                                          size="small"
                                        />
                                      </Form.Item>
                                      <span className="time-separator">TO</span>
                                      <Form.Item
                                        {...restField}
                                        name={[name, "end"]}
                                        noStyle
                                      >
                                        <TimePicker
                                          format="HH:mm"
                                          placeholder="01:00 PM"
                                          className="time-picker"
                                          use12Hours={false}
                                          size="small"
                                        />
                                      </Form.Item>
                                      <Button
                                        type="text"
                                        icon={<CloseOutlined />}
                                        size="small"
                                        onClick={() => remove(name)}
                                        danger
                                      />
                                    </div>
                                  ))}
                                  <Button
                                    type="dashed"
                                    onClick={() =>
                                      add({ start: null, end: null })
                                    }
                                    block
                                    size="small"
                                    className="add-slot-btn"
                                  >
                                    + Add Time Slot
                                  </Button>
                                </>
                              )}
                            </Form.List>
                          </div>
                        ))}
                      </div>
                    ),
                  },
                ]}
              />
            </Col>
          </Row>

          <Form.Item className="form-actions">
            <Space className="actions-space">
              <Button onClick={handleCancel}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {isEdit ? "Update" : "Add"} Branch
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </StyledBranchModalDiv>
    </Modal>
  );
};

export default AddEditBranchModal;
